// SVG swimlane diagram with Bézier arrows — per impl spec §6.2, D-57
// WD-DIAG-01: Arrow routing is known-visually-imperfect; shipped as-is per build plan.
// VoiceSpectrum.Web.T2 · v10.0.8.5 · 2026-05-17 · VoiceSpectrum AI

import { useEffect, useRef } from 'react';
import type { Workflow, WorkflowNode } from './data';

interface Props {
  workflow: Workflow;
  onCardClick: (node: WorkflowNode) => void;
}

interface CardBounds {
  left: number; right: number; top: number; bottom: number;
  center_x: number; center_y: number; width: number; height: number;
}

interface LaneBounds {
  bottom: number;
}

// ── cardRect ─────────────────────────────────────────────────────────────────
function cardRect(
  pane: HTMLDivElement,
  sel: string,
): CardBounds | null {
  const el = pane.querySelector(sel);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  const pr = pane.getBoundingClientRect();
  const left   = r.left - pr.left;
  const top    = r.top  - pr.top;
  const right  = left + r.width;
  const bottom = top  + r.height;
  return {
    left, right, top, bottom,
    center_x: left + r.width / 2,
    center_y: top  + r.height / 2,
    width: r.width,
    height: r.height,
  };
}

// ── drawArrow (D-57) ─────────────────────────────────────────────────────────
function drawArrow(
  svg: SVGSVGElement,
  pane: HTMLDivElement,
  selA: string,
  selB: string,
  laneA: string | null,
  laneB: string | null,
): void {
  // WD-DIAG-01: cross-lane routing is known-imperfect — shipped as-is per build plan
  const A = cardRect(pane, selA);
  const B = cardRect(pane, selB);
  if (!A || !B) return;

  const sx = A.right;
  const sy = A.center_y;
  const ex = B.left - 4;
  const ey = B.center_y;

  let d: string;
  if (laneA === laneB || !laneA || !laneB) {
    // Same-lane: simple cubic Bézier
    const cp1x = sx + 40;
    const cp1y = sy;
    const cp2x = ex - 40;
    const cp2y = ey;
    d = `M ${sx} ${sy} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${ex} ${ey}`;
  } else {
    // WD-DIAG-01: cross-lane routing — waypoint at sourceLane.bottom + 18
    const laneEl = pane.querySelector(`[data-lane="${laneA}"]`);
    const laneRect = laneEl ? laneEl.getBoundingClientRect() : null;
    const paneRect = pane.getBoundingClientRect();
    const laneBounds: LaneBounds = {
      bottom: laneRect ? (laneRect.bottom - paneRect.top) : sy + 80,
    };
    const midX  = (sx + ex) / 2;
    const wayY  = laneBounds.bottom + 18;
    d = `M ${sx} ${sy} C ${sx + 40} ${sy}, ${midX} ${wayY}, ${midX} ${wayY} S ${ex - 40} ${ey}, ${ex} ${ey}`;
  }

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', d);
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', '#94a3b8');
  path.setAttribute('stroke-width', '1.5');
  path.setAttribute('marker-end', 'url(#ah)');
  svg.appendChild(path);
}

function ensureArrowMarker(svg: SVGSVGElement): void {
  if (svg.querySelector('#ah')) return;
  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
  const marker = document.createElementNS('http://www.w3.org/2000/svg', 'marker');
  marker.setAttribute('id', 'ah');
  marker.setAttribute('markerWidth', '10');
  marker.setAttribute('markerHeight', '7');
  marker.setAttribute('refX', '9'); // D-57: tuned refX=9 for left-edge landing
  marker.setAttribute('refY', '3.5');
  marker.setAttribute('orient', 'auto');
  const poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
  poly.setAttribute('points', '0 0, 10 3.5, 0 7');
  poly.setAttribute('fill', '#94a3b8');
  marker.appendChild(poly);
  defs.appendChild(marker);
  svg.insertBefore(defs, svg.firstChild);
}

export default function WorkflowDiagram({ workflow, onCardClick }: Props) {
  const paneRef = useRef<HTMLDivElement>(null);
  const svgRef  = useRef<SVGSVGElement>(null);

  function redrawArrows() {
    const pane = paneRef.current;
    const svg  = svgRef.current;
    if (!pane || !svg) return;

    // Clear existing paths (keep defs)
    const paths = svg.querySelectorAll('path');
    paths.forEach(p => p.remove());
    ensureArrowMarker(svg);

    const pr = pane.getBoundingClientRect();
    svg.setAttribute('width',  String(pr.width));
    svg.setAttribute('height', String(pr.height));

    // Draw sequential arrows for each intent's node chain
    workflow.intents.forEach((intent, ii) => {
      intent.nodes.forEach((node, ni) => {
        if (ni < intent.nodes.length - 1) {
          drawArrow(svg, pane,
            `[data-intent="${intent.id}"][data-node="${node.id}"]`,
            `[data-intent="${intent.id}"][data-node="${intent.nodes[ni + 1].id}"]`,
            `intent-${ii}`,
            `intent-${ii}`,
          );
        }
      });
    });
  }

  useEffect(() => {
    redrawArrows();
    const handler = () => redrawArrows();
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow]);

  const nodeTypeColor: Record<string, string> = {
    Identity: 'var(--stripe-identity)',
    Steps:    'var(--stripe-steps)',
    Connect:  'var(--stripe-connect)',
    Confirm:  'var(--stripe-confirm)',
  };

  return (
    <div ref={paneRef} style={{ position: 'relative', overflowX: 'auto' }}>
      {/* SVG overlay for arrows */}
      <svg
        ref={svgRef}
        style={{
          position: 'absolute',
          top: 0, left: 0,
          pointerEvents: 'none',
          zIndex: 10,
        }}
      />

      {/* Swimlanes */}
      {workflow.intents.map((intent, ii) => (
        <div
          key={intent.id}
          data-lane={`intent-${ii}`}
          style={{
            border: '1px solid var(--t2-border)',
            borderRadius: 'var(--radius-md)',
            marginBottom: '1.25rem',
            overflow: 'hidden',
          }}
        >
          {/* Lane header */}
          <div style={{
            background: 'var(--t2-accent)',
            color: '#fff',
            padding: '0.5rem 1rem',
            fontWeight: 600,
            fontSize: '0.875rem',
          }}>
            {intent.name}
          </div>

          {/* Cards row */}
          <div style={{
            display: 'flex',
            flexWrap: 'nowrap',
            gap: '16px',
            padding: '1rem',
            alignItems: 'stretch',
          }}>
            {intent.nodes.map((node) => (
              <div
                key={node.id}
                data-intent={intent.id}
                data-node={node.id}
                onClick={() => onCardClick(node)}
                style={{
                  minWidth: '140px',
                  border: '1px solid var(--t2-border)',
                  borderLeft: `3px solid ${nodeTypeColor[node.type] ?? '#94a3b8'}`,
                  borderRadius: 'var(--radius-md)',
                  background: '#fff',
                  padding: '0.75rem',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.15s',
                  flexShrink: 0,
                }}
                onMouseEnter={e => ((e.currentTarget as HTMLDivElement).style.background = 'rgba(0,0,0,0.05)')}
                onMouseLeave={e => ((e.currentTarget as HTMLDivElement).style.background = '#fff')}
              >
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: nodeTypeColor[node.type] ?? '#94a3b8', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                  {node.type}
                </div>
                <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{node.name}</div>
                {!node.enabled && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--t2-ink-muted)', marginTop: '0.25rem' }}>Disabled</div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Terminal swimlane — D-57: 3 cards in one row (flex-wrap: nowrap) */}
      <div style={{ border: '1px solid var(--t2-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        <div style={{ background: '#7f1d1d', color: '#fff', padding: '0.5rem 1rem', fontWeight: 600, fontSize: '0.875rem' }}>
          Terminals
        </div>
        <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '24px', justifyContent: 'center', alignItems: 'stretch', padding: '1rem' }}>
          {['EndCall', 'Voicemail', 'Handoff'].map(t => (
            <div
              key={t}
              className="v18-card"
              style={{
                minWidth: '140px',
                border: '1px solid var(--t2-border)',
                borderLeft: '3px solid var(--stripe-terminal)',
                borderRadius: 'var(--radius-md)',
                background: '#fff',
                padding: '0.75rem',
                flex: '0 0 auto',
              }}
            >
              <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--stripe-terminal)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                Terminal
              </div>
              <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{t}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
